import axios from 'axios';

/**
 * Service for handling policy change requests
 */
class PolicyChangeService {
  /**
   * Submit a request for other policy changes
   * 
   * @param {Object} data - Form data
   * @param {Object} data.contactInfo - Contact information
   * @param {string} data.contactInfo.firstName - First name
   * @param {string} data.contactInfo.lastName - Last name
   * @param {string} data.contactInfo.policyNumber - Policy number
   * @param {string} data.contactInfo.phoneNumber - Phone number
   * @param {string} data.contactInfo.email - Email address
   * @param {Object} data.changeDetails - Change details
   * @param {string} data.changeDetails.changeType - Type of change
   * @param {string} data.changeDetails.description - Description of the change
   * @param {File} data.changeDetails.file - Supporting document (optional)
   * @param {Object} data.submissionDetails - Submission details
   * @param {string} data.submissionDetails.effectiveDate - Effective date for the change
   * @param {string} data.submissionDetails.comments - Additional comments
   * @returns {Promise<Object>} - API response
   */
  async submitOtherPolicyChange(data) {
    try {
      // Create form data for file upload
      const formData = new FormData();
      
      // Add contact info
      Object.entries(data.contactInfo).forEach(([key, value]) => {
        formData.append(`contactInfo[${key}]`, value);
      });
      
      // Add change details
      formData.append('changeDetails[changeType]', data.changeDetails.changeType);
      formData.append('changeDetails[description]', data.changeDetails.description);
      
      // Add file if present
      if (data.changeDetails.file) {
        formData.append('file', data.changeDetails.file);
      }
      
      // Add submission details
      Object.entries(data.submissionDetails).forEach(([key, value]) => {
        formData.append(`submissionDetails[${key}]`, value);
      });
      
      // Make API request
      const response = await axios.post('/api/policy-changes/other', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error submitting other policy change:', error);
      throw error;
    }
  }
}

// Singleton instance
const policyChangeService = new PolicyChangeService();

/**
 * Hook for using the policy change service
 * @returns {PolicyChangeService} Service instance
 */
export function usePolicyChangeService() {
  return policyChangeService;
}

/**
 * Hook specifically for other policy changes
 * @returns {Object} - Object with submitOtherPolicyChange method
 */
export function useOtherPolicyChangeService() {
  return {
    submitOtherPolicyChange: policyChangeService.submitOtherPolicyChange.bind(policyChangeService)
  };
} 